// @ts-nocheck
/*
 * Call-centre-admin-only Agent Performance Report.
 * Scoped to this call centre's child agents. Team-lead filter shows
 * agents assigned to that lead via agent_team_lead_config.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/ui/multi-select';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_PERFORMANCE_REPORT } from '@/service/url';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import moment from 'moment';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const pad = (n) => String(n).padStart(2, '0');

const formatDuration = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(sec)}s`;
  return `${m}m ${pad(sec)}s`;
};

const pctLabel = (value) => `${Number(value || 0).toFixed(2)}%`;

const dateToInputStr = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const inputStrToLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const getDefaultDates = () => {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(),
  };
};

const emptyTotals = () => ({
  total: 0,
  answered: 0,
  unanswered: 0,
  missed: 0,
  declined: 0,
  incoming: 0,
  outgoing: 0,
  connected: 0,
  total_talk_seconds: 0,
  avg_talk_seconds: 0,
  avg_handle_seconds: 0,
  total_wrap_seconds: 0,
  avg_wrap_seconds: 0,
  wrap_count: 0,
  answered_pct: 0,
  unanswered_pct: 0,
  missed_pct: 0,
  declined_pct: 0,
  connect_pct: 0,
  transfers: 0,
  conferences: 0,
  total_hold_seconds: 0,
  hold_count: 0,
  avg_hold_seconds: 0,
  total_mute_seconds: 0,
  mute_count: 0,
  avg_mute_seconds: 0,
  total_transfer_seconds: 0,
  avg_transfer_seconds: 0,
  total_conference_seconds: 0,
  avg_conference_seconds: 0,
  breaks_taken: 0,
  total_break_seconds: 0,
  avg_break_seconds: 0,
  exceeded_break_seconds: 0,
  exceeded_break_count: 0,
});

const pct = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 10000) / 100 : 0;

const totalsFromRows = (list) => {
  const acc = emptyTotals();
  (list || []).forEach((r) => {
    acc.total += Number(r.total) || 0;
    acc.answered += Number(r.answered) || 0;
    acc.unanswered += Number(r.unanswered) || 0;
    acc.missed += Number(r.missed) || 0;
    acc.declined += Number(r.declined) || 0;
    acc.incoming += Number(r.incoming) || 0;
    acc.outgoing += Number(r.outgoing) || 0;
    acc.connected += Number(r.connected) || 0;
    acc.total_talk_seconds += Number(r.total_talk_seconds) || 0;
    acc.total_wrap_seconds += Number(r.total_wrap_seconds) || 0;
    acc.wrap_count += Number(r.wrap_count) || 0;
    acc.transfers += Number(r.transfers) || 0;
    acc.conferences += Number(r.conferences) || 0;
    acc.total_hold_seconds += Number(r.total_hold_seconds) || 0;
    acc.hold_count += Number(r.hold_count) || 0;
    acc.total_mute_seconds += Number(r.total_mute_seconds) || 0;
    acc.mute_count += Number(r.mute_count) || 0;
    acc.total_transfer_seconds += Number(r.total_transfer_seconds) || 0;
    acc.total_conference_seconds += Number(r.total_conference_seconds) || 0;
    acc.breaks_taken += Number(r.breaks_taken) || 0;
    acc.total_break_seconds += Number(r.total_break_seconds) || 0;
    acc.exceeded_break_seconds += Number(r.exceeded_break_seconds) || 0;
    acc.exceeded_break_count += Number(r.exceeded_break_count) || 0;
  });
  acc.answered_pct = pct(acc.answered, acc.total);
  acc.unanswered_pct = pct(acc.unanswered, acc.total);
  acc.missed_pct = pct(acc.missed, acc.total);
  acc.declined_pct = pct(acc.declined, acc.total);
  acc.connect_pct = pct(acc.connected, acc.total);
  acc.avg_talk_seconds =
    acc.connected > 0 ? Math.round(acc.total_talk_seconds / acc.connected) : 0;
  acc.avg_handle_seconds =
    acc.connected > 0
      ? Math.round((acc.total_talk_seconds + acc.total_wrap_seconds) / acc.connected)
      : 0;
  acc.avg_wrap_seconds =
    acc.wrap_count > 0 ? Math.round(acc.total_wrap_seconds / acc.wrap_count) : 0;
  acc.avg_hold_seconds =
    acc.hold_count > 0 ? Math.round(acc.total_hold_seconds / acc.hold_count) : 0;
  acc.avg_mute_seconds =
    acc.mute_count > 0 ? Math.round(acc.total_mute_seconds / acc.mute_count) : 0;
  acc.avg_transfer_seconds =
    acc.transfers > 0 ? Math.round(acc.total_transfer_seconds / acc.transfers) : 0;
  acc.avg_conference_seconds =
    acc.conferences > 0
      ? Math.round(acc.total_conference_seconds / acc.conferences)
      : 0;
  acc.avg_break_seconds =
    acc.breaks_taken > 0 ? Math.round(acc.total_break_seconds / acc.breaks_taken) : 0;
  return acc;
};

const SUMMARY_CARDS = [
  { key: 'total', label: 'Total Calls', className: 'text-blue-600 dark:text-blue-400' },
  { key: 'incoming', label: 'Incoming', className: 'text-sky-600 dark:text-sky-400' },
  { key: 'outgoing', label: 'Outgoing', className: 'text-orange-600 dark:text-orange-400' },
  { key: 'answered', label: 'Answered', className: 'text-emerald-600 dark:text-emerald-400', pctKey: 'answered_pct' },
  { key: 'unanswered', label: 'Unanswered', className: 'text-muted-foreground', pctKey: 'unanswered_pct' },
  { key: 'missed', label: 'Missed', className: 'text-amber-600 dark:text-amber-400', pctKey: 'missed_pct' },
  { key: 'declined', label: 'Declined', className: 'text-rose-600 dark:text-rose-400', pctKey: 'declined_pct' },
  { key: 'connected', label: 'Connected', className: 'text-emerald-700 dark:text-emerald-300', pctKey: 'connect_pct' },
  { key: 'total_talk_seconds', label: 'Talk Time', className: 'text-foreground', isDuration: true },
  { key: 'avg_handle_seconds', label: 'Avg Handle', className: 'text-foreground', isDuration: true },
  { key: 'total_wrap_seconds', label: 'Total Wrap', className: 'text-foreground', isDuration: true },
  { key: 'avg_wrap_seconds', label: 'Avg Wrap', className: 'text-foreground', isDuration: true },
  { key: 'breaks_taken', label: 'Breaks Taken', className: 'text-foreground' },
  { key: 'total_break_seconds', label: 'Total Break', className: 'text-foreground', isDuration: true },
  { key: 'exceeded_break_seconds', label: 'Exceeded Break', className: 'text-rose-600 dark:text-rose-400', isDuration: true },
  { key: 'total_hold_seconds', label: 'Hold Time', className: 'text-foreground', isDuration: true },
  { key: 'total_mute_seconds', label: 'Mute Time', className: 'text-foreground', isDuration: true },
  { key: 'total_transfer_seconds', label: 'Transfer Time', className: 'text-foreground', isDuration: true },
  { key: 'total_conference_seconds', label: 'Conference Time', className: 'text-foreground', isDuration: true },
];

const AgentPerformanceReportPage = () => {
  const { start, end } = getDefaultDates();
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [teamLeadId, setTeamLeadId] = useState('');
  const [selectedAgents, setSelectedAgents] = useState([]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(emptyTotals());
  const [filteredRows, setFilteredRows] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [teamLeadOptions, setTeamLeadOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const minDate = dateToInputStr(moment().subtract(6, 'months').toDate());
  const maxDate = dateToInputStr(new Date());

  const selectedLead = useMemo(
    () => teamLeadOptions.find((t) => String(t.id) === String(teamLeadId)) || null,
    [teamLeadOptions, teamLeadId],
  );

  const filteredAgentOptions = useMemo(() => {
    const all = agentOptions.map((a) => ({
      value: String(a.id),
      label: a.agent_name || `Agent #${a.id}`,
    }));
    if (!selectedLead) return all;
    const allowed = new Set((selectedLead.agent_ids || []).map(String));
    return all.filter((o) => allowed.has(o.value));
  }, [agentOptions, selectedLead]);

  const handleTeamLeadChange = (value) => {
    setTeamLeadId(value);
    if (!value) return;
    const lead = teamLeadOptions.find((t) => String(t.id) === String(value));
    const allowed = new Set((lead?.agent_ids || []).map(String));
    setSelectedAgents((prev) => prev.filter((id) => allowed.has(id)));
  };

  const fetchReport = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError('');
    const nextStart = overrides.startDate ?? startDate;
    const nextEnd = overrides.endDate ?? endDate;
    const nextLead = overrides.teamLeadId !== undefined ? overrides.teamLeadId : teamLeadId;
    const nextAgents = overrides.selectedAgents ?? selectedAgents;

    const params = new URLSearchParams();
    const from = dateToInputStr(nextStart);
    const to = dateToInputStr(nextEnd);
    if (from) params.append('start_date', from);
    if (to) params.append('end_date', to);
    if (nextLead) params.append('team_lead_id', nextLead);
    if (nextAgents.length) params.append('agent_ids', nextAgents.join(','));

    try {
      const resp = await getApiCall(`${API_AGENT_PERFORMANCE_REPORT}?${params.toString()}`);
      setRows(Array.isArray(resp?.agents) ? resp.agents : []);
      setTotals(resp?.totals || emptyTotals());
      if (Array.isArray(resp?.agent_options)) setAgentOptions(resp.agent_options);
      if (Array.isArray(resp?.team_lead_options)) setTeamLeadOptions(resp.team_lead_options);
    } catch (err) {
      setRows([]);
      setTotals(emptyTotals());
      setError(typeof err === 'string' ? friendlyApiError(err) : 'Failed to load agent performance report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, teamLeadId, selectedAgents]);

  useEffect(() => {
    fetchReport();
    // Initial load only — subsequent loads go through Filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    const defaults = getDefaultDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
    setTeamLeadId('');
    setSelectedAgents([]);
    fetchReport({
      startDate: defaults.start,
      endDate: defaults.end,
      teamLeadId: '',
      selectedAgents: [],
    });
  };

  const displayTotals = useMemo(
    () => (loading ? totals : totalsFromRows(filteredRows)),
    [loading, totals, filteredRows],
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_name', header: 'Agent' },
      {
        accessorKey: 'access',
        header: 'Role',
        cell: ({ row }) =>
          row.original.access === 'team_lead' ? 'Team Lead' : 'Agent',
      },
      {
        accessorKey: 'team_leads',
        header: 'Team Lead',
        cell: ({ row }) => row.original.team_leads || '—',
      },
      { accessorKey: 'agent_number', header: 'Agent Number' },
      { accessorKey: 'total', header: 'Total', meta: { filter: 'number' } },
      { accessorKey: 'incoming', header: 'Incoming', meta: { filter: 'number' } },
      { accessorKey: 'outgoing', header: 'Outgoing', meta: { filter: 'number' } },
      { accessorKey: 'answered', header: 'Answered', meta: { filter: 'number' } },
      { accessorKey: 'unanswered', header: 'Unanswered', meta: { filter: 'number' } },
      { accessorKey: 'missed', header: 'Missed', meta: { filter: 'number' } },
      { accessorKey: 'declined', header: 'Declined', meta: { filter: 'number' } },
      { accessorKey: 'connected', header: 'Connected', meta: { filter: 'number' } },
      {
        accessorKey: 'answered_pct',
        header: 'Answered %',
        meta: { filter: 'percent' },
        cell: ({ row }) => pctLabel(row.original.answered_pct),
      },
      {
        accessorKey: 'unanswered_pct',
        header: 'Unanswered %',
        meta: { filter: 'percent' },
        cell: ({ row }) => pctLabel(row.original.unanswered_pct),
      },
      {
        accessorKey: 'missed_pct',
        header: 'Missed %',
        meta: { filter: 'percent' },
        cell: ({ row }) => pctLabel(row.original.missed_pct),
      },
      {
        accessorKey: 'declined_pct',
        header: 'Declined %',
        meta: { filter: 'percent' },
        cell: ({ row }) => pctLabel(row.original.declined_pct),
      },
      {
        accessorKey: 'connect_pct',
        header: 'Connect %',
        meta: { filter: 'percent' },
        cell: ({ row }) => pctLabel(row.original.connect_pct),
      },
      {
        accessorKey: 'total_talk_seconds',
        header: 'Talk Time',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_talk_seconds),
      },
      {
        accessorKey: 'avg_talk_seconds',
        header: 'Avg Talk',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_talk_seconds),
      },
      {
        accessorKey: 'avg_handle_seconds',
        header: 'Avg Handle',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_handle_seconds),
      },
      {
        accessorKey: 'avg_ring_seconds',
        header: 'Avg Ring',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_ring_seconds),
      },
      { accessorKey: 'transfers', header: 'Transfers', meta: { filter: 'number' } },
      {
        accessorKey: 'total_transfer_seconds',
        header: 'Transfer Time',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_transfer_seconds),
      },
      {
        accessorKey: 'avg_transfer_seconds',
        header: 'Avg Transfer',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_transfer_seconds),
      },
      { accessorKey: 'conferences', header: 'Conferences', meta: { filter: 'number' } },
      {
        accessorKey: 'total_conference_seconds',
        header: 'Conference Time',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_conference_seconds),
      },
      {
        accessorKey: 'avg_conference_seconds',
        header: 'Avg Conference',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_conference_seconds),
      },
      {
        accessorKey: 'total_hold_seconds',
        header: 'Hold Time',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_hold_seconds),
      },
      {
        accessorKey: 'avg_hold_seconds',
        header: 'Avg Hold',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_hold_seconds),
      },
      {
        accessorKey: 'total_mute_seconds',
        header: 'Mute Time',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_mute_seconds),
      },
      {
        accessorKey: 'avg_mute_seconds',
        header: 'Avg Mute',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_mute_seconds),
      },
      { accessorKey: 'breaks_taken', header: 'Breaks Taken', meta: { filter: 'number' } },
      {
        accessorKey: 'total_break_seconds',
        header: 'Total Break',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_break_seconds),
      },
      {
        accessorKey: 'avg_break_seconds',
        header: 'Avg Break',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_break_seconds),
      },
      {
        accessorKey: 'exceeded_break_seconds',
        header: 'Exceeded Break',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.exceeded_break_seconds),
      },
      {
        accessorKey: 'exceeded_break_count',
        header: 'Exceeded Breaks',
        meta: { filter: 'number' },
      },
      {
        accessorKey: 'total_wrap_seconds',
        header: 'Total Wrap',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.total_wrap_seconds),
      },
      {
        accessorKey: 'avg_wrap_seconds',
        header: 'Avg Wrap',
        meta: { filter: 'duration' },
        cell: ({ row }) => formatDuration(row.original.avg_wrap_seconds),
      },
      { accessorKey: 'wrap_count', header: 'Wrap Count', meta: { filter: 'number' } },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Agent Performance Report"
        description="Per-agent call outcomes, talk time, wrap-up, breaks, hold, mute, transfer and conference for this call centre's agents."
      />

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex shrink-0 flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              From
            </label>
            <CalendarDateInput
              value={dateToInputStr(startDate)}
              onChange={(e) => setStartDate(inputStrToLocalDate(e))}
              min={minDate}
              max={dateToInputStr(endDate) || maxDate}
              className="input-embossed h-10 w-[170px] rounded-md px-3 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              To
            </label>
            <CalendarDateInput
              value={dateToInputStr(endDate)}
              onChange={(e) => setEndDate(inputStrToLocalDate(e))}
              min={dateToInputStr(startDate) || minDate}
              max={maxDate}
              className="input-embossed h-10 w-[170px] rounded-md px-3 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Team Lead
            </label>
            <select
              value={teamLeadId}
              onChange={(e) => handleTeamLeadChange(e.target.value)}
              className="input-embossed h-10 w-[200px] rounded-md px-3 text-sm"
            >
              <option value="">All team leads</option>
              {teamLeadOptions.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.agent_name || `Lead #${lead.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <MultiSelect
              options={filteredAgentOptions}
              value={selectedAgents}
              onChange={setSelectedAgents}
              placeholder={teamLeadId ? 'Agents under lead' : 'All agents'}
              searchPlaceholder="Search agent…"
              emptyMessage={
                teamLeadId
                  ? 'No agents assigned to this team lead'
                  : 'No agents available'
              }
              maxChips={2}
              className="w-[260px]"
            />
          </div>
          <Button size="sm" onClick={fetchReport} disabled={loading}>
            Filter
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={loading}>
            Reset
          </Button>
        </div>

        {error ? <div className="mb-3 text-sm text-destructive">{error}</div> : null}

        <div className="mb-4 grid shrink-0 gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
          {SUMMARY_CARDS.map((card) => (
            <div key={card.key} className="rounded-lg border border-border/60 px-3 py-2.5">
              <div className="text-[10px] font-bold uppercase text-muted-foreground">
                {card.label}
              </div>
              <div className={'text-xl font-bold ' + card.className}>
                {card.isDuration
                  ? formatDuration(displayTotals[card.key])
                  : displayTotals[card.key] ?? 0}
              </div>
              {card.pctKey ? (
                <div className="text-[11px] text-muted-foreground">
                  {pctLabel(displayTotals[card.pctKey])}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1">
          <DataTable
            tableId="reports.agent-performance.per-agent"
            columns={columns}
            data={rows}
            isLoading={loading}
            searchPlaceholder="Search agents…"
            searchColumnId="agent_name"
            emptyTitle="No agent performance data"
            emptyDescription="No agent call data for the selected filters."
            pageSize={10}
            exportFilename="agent-performance-report.csv"
            initialSorting={[{ id: 'total', desc: true }]}
            initialColumnPinning={{ left: ['agent_name'] }}
            initialColumnVisibility={{
              access: false,
              agent_number: false,
              avg_ring_seconds: false,
              transfers: false,
              conferences: false,
              wrap_count: false,
              avg_transfer_seconds: false,
              avg_conference_seconds: false,
              avg_hold_seconds: false,
              avg_mute_seconds: false,
              avg_break_seconds: false,
              exceeded_break_count: false,
            }}
            onFilteredDataChange={setFilteredRows}
          />
        </div>
      </div>
    </FillPage>
  );
};

export default AgentPerformanceReportPage;
